import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DateField from '../components/DateField.jsx';
import { api, withQuery, money, date, dateTime, isoDate } from '../api.js';

/**
 * Доставки до клиенти.
 *
 * Логистиката се изпълнява в Деливери: продажбата при провеждане оставя
 * там чакаща задача, операторът я слага в маршрут, шофьорът я отметва.
 * Тук Тефтер показва докъде е стигнала всяка — прочетено от Деливери
 * (/api/deliveries → /api/pending-tasks/status там).
 */

export const DELIVERY_STATE = {
  pending: { label: 'чака маршрут', cls: 'badge warn' },
  scheduled: { label: 'в маршрут', cls: 'badge' },
  done: { label: 'доставено', cls: 'badge ok' },
  dismissed: { label: 'без доставка', cls: 'badge muted' },
  unknown: { label: 'изпратено', cls: 'badge muted' },
  unavailable: { label: 'Деливери не отговаря', cls: 'badge danger' },
};

/** Един ред за статуса — ползва се и в списъка, и в продажбата. */
export function DeliveryStateText({ d }) {
  if (!d) return null;
  const s = DELIVERY_STATE[d.state] ?? DELIVERY_STATE.unknown;
  const stops = d.stops ?? [];
  return (
    <span>
      <span className={s.cls}>{s.label}</span>
      {d.state === 'pending' && d.site && <span className="muted"> · {d.site}</span>}
      {stops.map((st, i) => (
        <span key={i} className="muted">
          {' · '}{st.site}: маршрут {date(st.routeDate)}, {st.driver}
          {st.done ? `, доставено ${dateTime(st.doneAt)}${st.doneBy ? ` (${st.doneBy})` : ''}` : ''}
        </span>
      ))}
      {d.state === 'dismissed' && d.at && <span className="muted"> · отказано {dateTime(d.at)}</span>}
      {d.state === 'unknown' && (
        <span className="muted"> · изпратено преди Деливери да пази връзката с маршрута</span>
      )}
    </span>
  );
}

/** Блокът в продажбата — показва се, когато тя е изпратена в Деливери. */
export function DocumentDeliveryStatus({ documentId, refreshKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/api/documents/${documentId}/delivery-status`).then(setD).catch(() => setD(null));
  }, [documentId, refreshKey]);
  if (!d || !d.sent) return null;
  return (
    <div className="notice">
      <strong>Доставка:</strong> изпратена в Деливери{d.sentAt ? ` на ${dateTime(d.sentAt)}` : ''} · <DeliveryStateText d={d} />
    </div>
  );
}

const FILTRI = [
  ['', 'Всички'],
  ['pending', 'Чакат маршрут'],
  ['scheduled', 'В маршрут'],
  ['done', 'Доставени'],
  ['dismissed', 'Без доставка'],
];

export default function Deliveries() {
  const [range, setRange] = useState(() => ({
    from: isoDate(new Date(Date.now() - 30 * 86400000)),
    to: isoDate(),
  }));
  const [filtar, setFiltar] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(withQuery('/api/deliveries', range)).then(setData).catch((e) => setError(e.message));
  }, [range]);

  const broi = useMemo(() => {
    const b = {};
    for (const r of data?.rows ?? []) b[r.delivery.state] = (b[r.delivery.state] ?? 0) + 1;
    return b;
  }, [data]);
  const redove = (data?.rows ?? []).filter((r) => !filtar || r.delivery.state === filtar);

  return (
    <>
      <div className="page-head">
        <h1>Доставки до клиенти</h1>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Проведената продажба за клиент с обект в Деливери отива там като чакаща задача;
        операторът я слага в маршрут, шофьорът я отметва при доставка. Тук се вижда докъде е
        стигнала всяка. Ръчно изпращане — бутон „Изпрати в Деливери“ в продажбата.
      </p>
      <div className="toolbar">
        <label className="field">
          От
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        {FILTRI.map(([k, l]) => (
          <button key={k} type="button" className={filtar === k ? 'primary' : ''} onClick={() => setFiltar(k)}>
            {l}{k && broi[k] ? ` (${broi[k]})` : ''}
          </button>
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      {data && !data.deliveryReachable && (
        <div className="error">Деливери не отговаря — статусите не могат да се прочетат в момента.</div>
      )}
      <div className="card">
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Изпратена</th>
              <th style={{ width: 110 }}>Продажба</th>
              <th>Клиент</th>
              <th className="num">Сума</th>
              <th>Доставка</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={5} className="empty">Зареждане…</td></tr>}
            {redove.map((r) => (
              <tr key={r.id}>
                <td>{dateTime(r.delivery_notified_at)}</td>
                <td><Link to={`/documents/${r.id}`}>{r.doc_number}</Link></td>
                <td>{r.partner_id ? <Link to={`/partners/${r.partner_id}`}>{r.partner_name}</Link> : '—'}</td>
                <td className="num">{money(r.total_base)}</td>
                <td><DeliveryStateText d={r.delivery} /></td>
              </tr>
            ))}
            {data && redove.length === 0 && (
              <tr><td colSpan={5} className="empty">Няма доставки за периода</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
